import { Badge, Box, Button, HStack, Text, VStack } from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { TicketUpdate } from '@/types/parentTicket'
import OverviewSectionCard from './OverviewSectionCard'
import {
  formatUpdateRelative,
  pickLatestUpdate,
  updateAuthorInitial,
  updateAuthorName,
} from './formatUpdateMeta'

type Props = {
  updates: TicketUpdate[]
  onViewAll: () => void
}

export default function OverviewActivitySnippet({ updates, onViewAll }: Props) {
  const colors = useThemeColors()
  const latest = pickLatestUpdate(updates)

  if (!latest) {
    return (
      <OverviewSectionCard
        title="Latest activity"
        rightAction={
          <Button size="xs" variant="outline" fontWeight="normal" onClick={onViewAll}>
            View all →
          </Button>
        }
      >
        <Text fontSize="sm" color={colors.textSecondary}>
          No activity yet.
        </Text>
      </OverviewSectionCard>
    )
  }

  const author = updateAuthorName(latest)
  const initial = updateAuthorInitial(author)
  const isInternal = latest.is_internal ?? latest.isInternal

  return (
    <OverviewSectionCard
      title="Latest activity"
      rightAction={
        <Button size="xs" variant="outline" fontWeight="normal" onClick={onViewAll}>
          View all →
        </Button>
      }
    >
      <HStack align="flex-start" spacing={2.5}>
        <Box
          w="30px"
          h="30px"
          borderRadius="full"
          bg="blue.50"
          display="grid"
          placeItems="center"
          flexShrink={0}
        >
          <Text fontWeight="500" color="blue.600" fontSize="xs">
            {initial}
          </Text>
        </Box>
        <Box minW={0} flex="1">
          <HStack spacing={2} flexWrap="wrap" mb={0.5}>
            <Text fontSize="sm" lineHeight="short">
              <Text as="span" fontWeight="500" color={colors.text}>
                {author}
              </Text>
              <Text as="span" color={colors.textMuted}>
                {' · '}
                {formatUpdateRelative(latest.created_at)}
              </Text>
            </Text>
            {isInternal ? (
              <Badge colorScheme="orange" fontSize="0.6rem" borderRadius="md">
                Internal
              </Badge>
            ) : null}
            {latest.type !== 'comment' ? (
              <Badge colorScheme="gray" fontSize="0.6rem" borderRadius="md" textTransform="capitalize">
                {latest.type.replace('_', ' ')}
              </Badge>
            ) : null}
          </HStack>
          <Text fontSize="sm" color={colors.textSecondary} noOfLines={3} whiteSpace="pre-wrap" lineHeight="1.6">
            {latest.content?.trim() || '—'}
          </Text>
        </Box>
      </HStack>
    </OverviewSectionCard>
  )
}
